import { 
  Inbox, 
  FileText, 
  Users, 
  Package, 
  Wrench,
  ClipboardList,
  Calendar,
  Bell,
  Search,
  FolderOpen
} from "lucide-react";
import { Button } from "@/components/ui/button";

const icons = {
  inbox: Inbox,
  documents: FileText,
  users: Users,
  packages: Package,
  maintenance: Wrench,
  tasks: ClipboardList,
  calendar: Calendar,
  notifications: Bell,
  search: Search,
  folder: FolderOpen
};

/**
 * Reusable Empty State Component
 * Displays a friendly message when there's no data to show
 */
export function EmptyState({ 
  icon = "inbox", 
  title = "No data yet",
  description = "Items will appear here once they're added.",
  action,
  actionLabel = "Get Started",
  onAction,
  compact = false
}) {
  const IconComponent = icons[icon] || Inbox;
  
  return (
    <div className={`flex flex-col items-center justify-center ${compact ? 'py-8' : 'py-16'} px-4`}>
      {/* Animated Icon Container */}
      <div className={`relative ${compact ? 'mb-3' : 'mb-6'}`}>
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-cyan-400/20 rounded-full blur-xl animate-pulse" />
        
        {/* Icon circle */}
        <div className={`relative ${compact ? 'w-16 h-16' : 'w-24 h-24'} bg-gradient-to-br from-slate-50 to-slate-100 rounded-full flex items-center justify-center border border-slate-200 shadow-sm`}>
          <IconComponent className={`${compact ? 'w-7 h-7' : 'w-10 h-10'} text-slate-400`} strokeWidth={1.5} />
        </div>
        
        {/* Decorative dots */}
        {!compact && (
          <>
            <div className="absolute -top-2 -right-2 w-3 h-3 bg-blue-200 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="absolute -bottom-1 -left-3 w-2 h-2 bg-cyan-200 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="absolute top-1/2 -right-4 w-2 h-2 bg-purple-200 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
          </>
        )}
      </div>
      
      {/* Text Content */}
      <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-slate-900 text-center mb-1`}>
        {title}
      </h3>
      <p className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500 text-center max-w-sm`}>
        {description}
      </p>
      
      {/* Action Button */}
      {(action || onAction) && (
        <Button 
          onClick={onAction}
          className={`${compact ? 'mt-3' : 'mt-6'} bg-blue-600 hover:bg-blue-700 text-white`}
          size={compact ? "sm" : "default"}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

/**
 * Empty State for Search Results
 */
export function SearchEmptyState({ query }) {
  return (
    <EmptyState 
      icon="search"
      title="No results found"
      description={query ? `No items match "${query}". Try adjusting your search or filters.` : "Try searching for something."}
    />
  );
}

/**
 * Empty State for Loading Error
 */
export function ErrorState({ 
  title = "Something went wrong", 
  description = "We couldn't load the data. Please try again.",
  onRetry 
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-slate-900 text-center mb-1">{title}</h3>
      <p className="text-sm text-slate-500 text-center max-w-sm mb-4">{description}</p>
      {onRetry && (
        <Button onClick={onRetry} variant="outline">
          Try Again
        </Button>
      )}
    </div>
  );
}

export default EmptyState;
